import { Divider } from 'primereact/divider';
import { useTranslation } from 'react-i18next';

import RestrictedAccessCard from '../../shared/components/restricted-access-card/RestrictedAccessCard';

type BlurredComponentDetailsPageProps = {
  /**
   * The card prompts for a license upgrade, so it only belongs to the restricted
   * case. Other reasons for withholding the data show the blurred placeholder
   * on its own.
   */
  showRestrictedAccessCard?: boolean;
};

const BlurredComponentDetailsPage = ({
  showRestrictedAccessCard = true,
}: BlurredComponentDetailsPageProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className='flex w-full justify-content-center '>
      <div className='flex w-full relative'>
        <div className='blurred flex flex-column relative gap-8 mt-2 md:mt-4 w-12 md:w-8'>
          {/* General Details */}
          <div className='flex flex-column gap-4'>
            <h4>
              <div className='flex p-3 bg-gray-100 md:w-3 w-9 border-round-2xl' />
            </h4>

            <div className='grid grid-nogutter gap-5 md:gap-0'>
              {/* Supplier ID */}
              <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-7 border-round-2xl' />
              </div>

              {/* Your ID */}
              <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-7 border-round-2xl' />
              </div>

              {/* Supplier */}
              <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-7 border-round-2xl' />
              </div>

              {/* Location */}
              <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-7 border-round-2xl' />
              </div>
            </div>

            {/* Component Description */}
            <div className='flex flex-column md:gap-2 gap-1'>
              <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />

              <div className='flex p-2 bg-gray-50 w-full border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-full border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
            </div>
          </div>

          <Divider className='p-0 m-0 visible md:hidden' />

          {/* Active PCF */}
          <div className='flex flex-column gap-4'>
            <h4>
              <div className='flex p-3 bg-gray-100 md:w-2 w-8 border-round-2xl' />
            </h4>

            {/* PCF ID */}
            <div className='grid grid-nogutter gap-4 md:gap-0'>
              <div className='col-12 md:col-6 md:pr-4 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-7 border-round-2xl' />
              </div>

              {/* Date Created */}
              <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-5 border-round-2xl' />
              </div>

              {/* Declared Unit */}
              <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-5 border-round-2xl' />
              </div>
            </div>

            <div className='grid grid-nogutter gap-5 md:gap-0'>
              {/* Cross-sectoral Standard */}
              <div className='col-12 md:col-6 md:pr-4 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex flex-wrap gap-1 w-full'>
                  <div className='flex p-3 bg-gray-50 w-2 border-round-lg' />
                  <div className='flex p-3 bg-gray-50 w-3 border-round-lg' />
                  <div className='flex p-3 bg-gray-50 w-4 border-round-lg' />
                  <div className='flex p-3 bg-gray-50 w-2 border-round-lg' />
                  <div className='flex p-3 bg-gray-50 w-4 border-round-lg' />
                  <div className='flex p-3 bg-gray-50 w-1 border-round-lg' />
                </div>
              </div>

              {/* Reference Period */}
              <div className='col-12 md:col-6 flex flex-column md:gap-2 gap-1'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-5 border-round-2xl' />
              </div>
            </div>

            <div className='grid grid-nogutter gap-4 md:gap-0'>
              {/* PCF excl. */}
              <div className='col-12 md:col-6 md:pr-4 flex flex-column md:gap-2 gap-1 text-primary'>
                <div className='flex p-2 bg-primary-400 w-8 border-round-2xl' />
                <div className='flex p-2 bg-primary-200 w-4 border-round-2xl' />
              </div>

              {/* PCF incl. */}
              <div className='col-12 md:col-6 md:pr-4 flex flex-column md:gap-2 gap-1 text-primary'>
                <div className='flex p-2 bg-gray-100 w-8 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-4 border-round-2xl' />
              </div>
            </div>

            {/* Disclaimer */}
            <div className='flex flex-column md:gap-2 gap-1'>
              <div className='w-full md:pr-4 flex flex-column md:gap-2 gap-1 text-primary'>
                <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-full border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-full border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-2 border-round-2xl' />
              </div>
            </div>
          </div>
        </div>
        {showRestrictedAccessCard && (
          <RestrictedAccessCard text={t('componentDetailsPage.restrictedAccessCardText')} />
        )}
      </div>
    </div>
  );
};

export default BlurredComponentDetailsPage;
